import type { Allergen } from "./constants";

// Nutrition of a dish from its declared recipe (docs/BAZA_INGREDIENTE.md). Deterministic, no AI: every number is a
// source value per 100 g (official table, label, producer sheet) × the grams the restaurant confirmed. The result is
// an estimate (cooking changes water and fat), shown as such.

export const NUTRIENTS = [
  "energy_kcal",
  "fat",
  "saturated_fat",
  "carbohydrates",
  "sugars",
  "fiber",
  "protein",
  "salt",
] as const;
export type Nutrient = (typeof NUTRIENTS)[number];
/** Values per 100 g (or 100 ml); null = the source does not give it. */
export type Per100 = Record<Nutrient, number | null>;

export interface RecipeLine {
  grams: number;
  per100: Per100;
  allergens: readonly Allergen[];
  /** "May contain" (cross-contamination), kept apart from the ingredients. */
  traces?: readonly Allergen[];
  additives?: readonly string[];
  frozen?: boolean;
}

export interface DishNutrition {
  /** Weight the values refer to: the cooked weight when given, else the sum of the ingredients. */
  grams: number;
  perPortion: Per100;
  per100g: Per100;
  allergens: Allergen[];
  traces: Allergen[];
  additives: string[];
  frozen: boolean;
  /** Nutrients some ingredient does not give: their totals are lower bounds, shown as incomplete. */
  incomplete: Nutrient[];
}

const round = (n: number, digits: number) => Math.round(n * 10 ** digits) / 10 ** digits;
/** Label precision: energy in whole kcal, salt to 0.01 g, the rest to 0.1 g. */
const precision = (n: Nutrient) => (n === "energy_kcal" ? 0 : n === "salt" ? 2 : 1);

/**
 * Sums a recipe. `cookedGrams` (the weight served, when the restaurant gives it) turns the totals into values per
 * 100 g of the finished dish; without it, per 100 g of the raw ingredients.
 */
export function dishNutrition(lines: readonly RecipeLine[], cookedGrams?: number | null): DishNutrition {
  const raw = lines.reduce((g, l) => g + Math.max(0, l.grams), 0);
  const grams = cookedGrams && cookedGrams > 0 ? cookedGrams : raw;
  const perPortion = {} as Per100;
  const per100g = {} as Per100;
  const incomplete: Nutrient[] = [];
  for (const n of NUTRIENTS) {
    let total = 0;
    let known = false;
    for (const l of lines) {
      const v = l.per100[n];
      if (v === null || !Number.isFinite(v)) {
        if (l.grams > 0) incomplete.push(n);
        continue;
      }
      known = true;
      total += (v * Math.max(0, l.grams)) / 100;
    }
    perPortion[n] = known ? round(total, precision(n)) : null;
    per100g[n] = known && grams > 0 ? round((total * 100) / grams, precision(n)) : null;
  }
  const union = <T>(pick: (l: RecipeLine) => readonly T[] | undefined) => [
    ...new Set(lines.flatMap((l) => (l.grams > 0 ? (pick(l) ?? []) : []))),
  ];
  const allergens = union((l) => l.allergens);
  return {
    grams,
    perPortion,
    per100g,
    allergens,
    traces: union((l) => l.traces).filter((a) => !allergens.includes(a)),
    additives: union((l) => l.additives).sort(),
    frozen: lines.some((l) => l.grams > 0 && l.frozen),
    incomplete: [...new Set(incomplete)],
  };
}
